import { useEffect, useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

/* ===========================
   TIPOS
=========================== */

export interface ReciboDetalle {
  id_recibo: string;
  periodo: string;
  base: number;
  iva: number;
  total: number;
  url: string;
}

export interface Gasto {
  id_gasto: string;
  id_empresa: string;
  concepto: string;
  tipo_gasto: string;
  categoria_gasto: string;

  id_tipo_gasto: string;
  id_categoria_gasto: string;

  fecha_inicio: string;
  total_mes: number;
  iva_porcentaje: number;
  base_imponible: number;
  url_recibo?: string | null;

  activo: boolean;
  recibos: ReciboDetalle[];
}

export interface TipoGasto {
  id_tipo_gasto: string;
  nombre: string;
}

export interface CategoriaGasto {
  id_categoria_gasto: string;
  nombre: string;
}

/* ============================================================
   LISTADO GASTOS
============================================================ */

export const useGastosPorEmpresa = (
  empresaId: string,
  month: number | null,
  year: number
) => {
  const [gastos, setGastos] = useState<Gasto[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGastos = useCallback(async () => {
    if (!empresaId) return;

    setLoading(true);

    let from: string;
    let to: string;

    if (month) {
      const lastDay = new Date(year, month, 0).getDate();
      from = `${year}-${String(month).padStart(2, '0')}-01`;
      to = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    } else {
      from = `${year}-01-01`;
      to = `${year}-12-31`;
    }

    const { data, error } = await supabase.rpc('get_gastos_detalle', {
      p_empresa: empresaId,
      p_from: from,
      p_to: to,
    });

    if (error) {
      Sentry.captureException(error, { extra: { empresaId, month, year } });
      setGastos([]);
    } else {
      setGastos(data || []);
    }

    setLoading(false);
  }, [empresaId, month, year]);

  useEffect(() => {
    fetchGastos();
  }, [fetchGastos]);

  return { gastos, loading, refetch: fetchGastos };
};

/* ============================================================
   CRUD + STORAGE + COMPRESIÓN
============================================================ */

export const useExpenses = (empresaId: string) => {
  const [tiposGasto, setTiposGasto] = useState<TipoGasto[]>([]);
  const [categoriasGasto, setCategoriasGasto] = useState<CategoriaGasto[]>([]);
  const loadedRef = useRef(false);

  const nowIso = () => new Date().toISOString();
  const getUserId = () => localStorage.getItem('id_usuario');

  /* ---------- Combos ---------- */

  const fetchCombos = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const [
      { data: tipos, error: tiposError },
      { data: categorias, error: categoriasError },
    ] = await Promise.all([
      supabase.from('vista_cb_tipo_gasto').select('*'),
      supabase.from('vista_cb_categoria_gasto').select('*'),
    ]);

    if (tiposError) {
      Sentry.captureException(tiposError, { extra: { empresaId } });
    } else {
      setTiposGasto(tipos || []);
    }

    if (categoriasError) {
      Sentry.captureException(categoriasError, { extra: { empresaId } });
    } else {
      setCategoriasGasto(categorias || []);
    }
  }, [empresaId]);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  /* =====================================================
     HELPERS TIPO GASTO
  ==================================================== */

  const getTipoIdByNombre = (nombre: 'fijo' | 'variable') => {
    const found = tiposGasto.find(
      (t) => t.nombre.toLowerCase() === nombre.toLowerCase()
    );
    if (!found) throw new Error(`Tipo gasto ${nombre} no encontrado`);
    return found.id_tipo_gasto;
  };

  /* =====================================================
     IMAGEN → PDF
     Convierte una foto de factura en un PDF A4, centrando
     la imagen y manteniendo proporción. La imagen se pasa
     por canvas para comprimirla a JPEG antes de incrustarla.
  ==================================================== */

  const imagenAPdf = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      img.onload = async () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const { jsPDF } = await import('jspdf');

          const orientation = img.width > img.height ? 'l' : 'p';
          const pdf = new jsPDF({ orientation, unit: 'px', format: 'a4' });

          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();

          // Escalar para que quepa en la página manteniendo proporción
          const ratio = Math.min(pageW / img.width, pageH / img.height);
          const w = img.width * ratio;
          const h = img.height * ratio;
          const x = (pageW - w) / 2;
          const y = (pageH - h) / 2;

          // Comprimir la imagen vía canvas → JPEG
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext('2d')!.drawImage(img, 0, 0);
          const jpegData = canvas.toDataURL('image/jpeg', 0.7);

          pdf.addImage(jpegData, 'JPEG', x, y, w, h);
          const blob = pdf.output('blob');

          resolve(
            new File([blob], file.name.replace(/\.\w+$/, '.pdf'), {
              type: 'application/pdf',
            })
          );
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image load error'));
      };
    });

  /* =====================================================
     HELPERS STORAGE
  ==================================================== */

  const getStoragePathFromPublicUrl = (publicUrl: string): string | null => {
    const marker = '/storage/v1/object/public/bookoll/';
    const idx = publicUrl.indexOf(marker);
    if (idx === -1) return null;
    return publicUrl.slice(idx + marker.length);
  };

  const deleteFileByPublicUrl = async (publicUrl: string) => {
    const path = getStoragePathFromPublicUrl(publicUrl);
    if (!path) return;

    const { error } = await supabase.storage.from('bookoll').remove([path]);
    if (error) throw error;
  };

  /* =====================================================
     UPLOAD
     Si el archivo es una imagen (foto de factura), se
     convierte a PDF antes de subir. Si ya es un PDF u otro
     formato, se sube tal cual.
  ==================================================== */

  const uploadFile = async (file: File, fecha: string): Promise<string> => {
    let finalFile = file;

    if (file.type.startsWith('image/')) {
      finalFile = await imagenAPdf(file);
    }

    const [year, month] = fecha.split('-');
    const ext = finalFile.name.split('.').pop();
    const path = `gastos/${empresaId}/${year}_${month}/${crypto.randomUUID()}.${ext}`;

    const { error } = await supabase.storage
      .from('bookoll')
      .upload(path, finalFile);

    if (error) throw error;

    const { data } = supabase.storage.from('bookoll').getPublicUrl(path);
    return data.publicUrl;
  };

  /* =====================================================
     CAMBIAR TIPO GASTO (DRAG & DROP)
  ==================================================== */

  const cambiarTipoGasto = async (
    id_gasto: string,
    tipo: 'fijo' | 'variable'
  ) => {
    const id_tipo_gasto = getTipoIdByNombre(tipo);
    const idUsuario = getUserId();

    return supabase
      .from('gastos')
      .update({
        id_tipo_gasto,
        usuario_edicion: idUsuario,
        fecha_edicion: nowIso(),
      })
      .eq('id_gasto', id_gasto);
  };

  /* =====================================================
     CREAR
  ==================================================== */

  const crearGasto = async (payload: {
    concepto: string;
    fecha: string;
    base_imponible: number;
    iva_porcentaje: number;
    archivo?: File | null;
    id_tipo_gasto: string;
    id_categoria_gasto: string;
  }) => {
    const idUsuario = getUserId();

    let url_recibo: string | null = null;
    if (payload.archivo) {
      url_recibo = await uploadFile(payload.archivo, payload.fecha);
    }

    const iva_importe = Number(
      (payload.base_imponible * (payload.iva_porcentaje / 100)).toFixed(2)
    );
    const total = Number((payload.base_imponible + iva_importe).toFixed(2));

    return supabase.from('gastos').insert({
      id_gasto: crypto.randomUUID(),
      id_empresa: empresaId,
      concepto: payload.concepto,
      fecha: payload.fecha,
      base_imponible: payload.base_imponible,
      iva_porcentaje: payload.iva_porcentaje,
      iva_importe,
      total,
      url_recibo,
      id_tipo_gasto: payload.id_tipo_gasto,
      id_categoria_gasto: payload.id_categoria_gasto,
      activo: true,
      usuario_creacion: idUsuario,
    });
  };

  /* =====================================================
     EDITAR
  ==================================================== */

  const editarGasto = async (
    id_gasto: string,
    payload: {
      concepto: string;
      fecha: string;
      base_imponible: number;
      iva_porcentaje: number;
      archivo?: File | null;
      removeReceipt?: boolean;
      id_tipo_gasto: string;
      id_categoria_gasto: string;
    }
  ) => {
    const idUsuario = getUserId();

    const { data: current } = await supabase
      .from('gastos')
      .select('url_recibo')
      .eq('id_gasto', id_gasto)
      .single();

    const oldUrl = (current as any)?.url_recibo as string | null | undefined;

    if (payload.removeReceipt && oldUrl) await deleteFileByPublicUrl(oldUrl);

    let url_recibo: string | undefined;
    if (payload.archivo) {
      if (oldUrl) await deleteFileByPublicUrl(oldUrl);
      url_recibo = await uploadFile(payload.archivo, payload.fecha);
    }

    const iva_importe = Number(
      (payload.base_imponible * (payload.iva_porcentaje / 100)).toFixed(2)
    );
    const total = Number((payload.base_imponible + iva_importe).toFixed(2));

    const update: any = {
      concepto: payload.concepto,
      fecha: payload.fecha,
      base_imponible: payload.base_imponible,
      iva_porcentaje: payload.iva_porcentaje,
      iva_importe,
      total,
      id_tipo_gasto: payload.id_tipo_gasto,
      id_categoria_gasto: payload.id_categoria_gasto,
      usuario_edicion: idUsuario,
      fecha_edicion: nowIso(),
    };

    if (url_recibo) update.url_recibo = url_recibo;
    if (payload.removeReceipt && !payload.archivo) update.url_recibo = null;

    return supabase.from('gastos').update(update).eq('id_gasto', id_gasto);
  };

  /* =====================================================
     COPIAR
  ==================================================== */

  const copiarGasto = async (id_gasto: string, nuevaFecha: string) => {
    const idUsuario = getUserId();

    const { data, error } = await supabase
      .from('gastos')
      .select(
        'concepto, base_imponible, iva_porcentaje, id_tipo_gasto, id_categoria_gasto'
      )
      .eq('id_gasto', id_gasto)
      .single();

    if (error || !data) throw error || new Error('Gasto no encontrado');

    const iva_importe = Number(
      (data.base_imponible * (data.iva_porcentaje / 100)).toFixed(2)
    );
    const total = Number((data.base_imponible + iva_importe).toFixed(2));

    return supabase.from('gastos').insert({
      id_gasto: crypto.randomUUID(),
      id_empresa: empresaId,
      concepto: data.concepto,
      fecha: nuevaFecha,
      base_imponible: data.base_imponible,
      iva_porcentaje: data.iva_porcentaje,
      iva_importe,
      total,
      id_tipo_gasto: data.id_tipo_gasto,
      id_categoria_gasto: data.id_categoria_gasto,
      url_recibo: null,
      activo: true,
      usuario_creacion: idUsuario,
    });
  };

  /* =====================================================
     DELETE (REAL)
     Borrado real: elimina el archivo del recibo de Storage
     y la fila del gasto de la base de datos.
  ==================================================== */

  const eliminarGasto = async (id_gasto: string) => {
    // 1. Leer la url del recibo para poder borrar el archivo
    const { data: current, error: readError } = await supabase
      .from('gastos')
      .select('url_recibo')
      .eq('id_gasto', id_gasto)
      .single();

    if (readError) {
      Sentry.captureException(readError, { extra: { id_gasto } });
    }

    const url = (current as any)?.url_recibo as string | null | undefined;

    // 2. Borrar el archivo de Storage si existe (no bloquea el borrado del gasto)
    if (url) {
      try {
        await deleteFileByPublicUrl(url);
      } catch (err) {
        Sentry.captureException(err, { extra: { id_gasto, url } });
      }
    }

    // 3. Borrar la fila del gasto
    return supabase.from('gastos').delete().eq('id_gasto', id_gasto);
  };

  return {
    tiposGasto,
    categoriasGasto,
    crearGasto,
    editarGasto,
    copiarGasto,
    eliminarGasto,
    cambiarTipoGasto,
    refetchCombos: fetchCombos,
  };
};